import { codLabel, money } from '../lib/money';
import { Store } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 一单在列表里长什么样 —— 「可领取」和「今日各栏」共用这一份。
 *
 * ★ 抽出来是因为今天刚吃过「两边各写一份」的亏:司机端的联系人标记
 *   自己写了一套文案(「电话打不通」),和客服看到的(「电话号码不对」)
 *   说的不是一件事,而谁也发现不了。
 *   shared/order/labels.ts 顶上那句警告写的就是这个。
 *
 * ★ 用户原话:「其它标签格式中的列表显示方式也应该和可领取一样吧,
 *   现在只显示名字和订单号」。只给名字和单号的话,司机在「待取货」
 *   那一栏排今天的顺序时判断不了哪几单顺路 —— 他得点进每一单看。
 */

export interface TaskCardData {
  receiverName: string;
  receiverPhone?: string | null;
  receiverAddress: string;
  /** 取货之后地址被改过的那一刻 —— 有值就在地址上标出来 */
  addressChangedAt?: string | null;
  pickupName?: string | null;
  pickupAddress?: string | null;
  zone?: string | null;
  packageCount?: number;
  weightGram?: number | null;
  codAmount?: string | number | null;
  /** 要代收但金额待定 —— 卡片上标「代收 金额待定」,不能整行不出现 */
  codPending?: boolean;
  /** 这一单给多少 —— 只有「可领取」那一栏给,手上的单不重复显示 */
  payout?: string | number | null;
  currency?: string;
  /**
   * 智能推荐路线里排第几(1 起)。
   *
   * ★ 标在卡片最前面,而不是夹在中间:司机扫列表时找的就是
   *   「下一个是几号」,它得是这一眼里最先看到的东西。
   */
  routeSeq?: number | null;
  /** 预计几点到 */
  routeEtaAt?: string | null;
}

export function TaskCardBody({
  data,
  showPickup,
}: {
  data: TaskCardData;
  /** 发货那一段展不展开 —— 由列表统一控制,见下面的说明 */
  showPickup: boolean;
}) {
  const cod = Number(data.codAmount ?? 0);
  const payout = Number(data.payout ?? 0);

  return (
    <div className="min-w-0 flex-1">
      {/*
        ★ 推荐顺序的序号 + 预计到达时间。
          只在规划过之后出现 —— 没规划时多一个空位置是纯噪音。
      */}
      {data.routeSeq != null && (
        <div className="mb-1 flex items-center gap-2">
          <span
            className="inline-flex h-6 min-w-6 items-center justify-center rounded-full
                       bg-brand-600 px-1.5 text-xs font-bold text-white"
          >
            {data.routeSeq}
          </span>
          {data.routeEtaAt && (
            <span className="text-xs text-slate-500">
              预计{' '}
              {new Date(data.routeEtaAt).toLocaleTimeString('zh-CN', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}
            </span>
          )}
        </div>
      )}
      {/*
        ★ 发货那一段默认收起来。
          用户原话:「发货的信息折叠」。绝大多数单是从同一个仓库发的 ——
          每张卡都把它完整写一遍,真正要看的收货方反而被挤到下面。
          收起来之后一屏能多看两三单,而这一屏的用途正是「扫一遍今天有什么」。
      */}
      {showPickup && (
        <>
          <div className="text-xs text-slate-500"><InlineIcon icon={Store} /> 发货</div>
          <div className="break-words text-slate-900 dark:text-slate-100">
            {data.pickupName ?? '取货点'}
          </div>
          {data.pickupAddress && (
            <div className="mb-2 break-words text-sm text-slate-600 dark:text-slate-400">
              {data.pickupAddress}
            </div>
          )}
        </>
      )}

      {/*
        ★ 收件人名字用红色。
          到了门口要找的是**这个人**,而原来它和地址、单号一个颜色 ——
          司机在门口那几秒要重新扫一遍才能把它挑出来。
          红色在这一屏是空的(代收是琥珀色),拿来标这一样不会和别的抢。
      */}
      <div className="break-words font-medium text-red-600 dark:text-red-400">
        {data.receiverName}
        {data.receiverPhone && (
          <span className="ml-2 font-mono text-xs font-normal text-slate-400">
            {data.receiverPhone}
          </span>
        )}
      </div>
      {/*
        ★ 地址**不截断**。接口给的是完整的,而地址的后半截
          (门牌、楼层、区)恰恰是司机判断顺不顺路要看的那部分。
          一行放不下就折行,多占的两行远比看不全划算。
      */}
      <div className="break-words text-sm text-slate-600 dark:text-slate-400">
        {data.receiverAddress}
      </div>
      {/*
        ★ 「地址已更新」—— 只在**取货之后**被改过时出现。
 
          货在他车上而目的地变了。任务列表每 30 秒会拉到新地址,
          **但他不会注意到**:他记的是「下一站去皇后街」,
          而这行字悄悄变成了别的路名,照着脑子里那个地址开过去,
          到了才发现不对。
 
        ★ 用红底而不是一行小灰字:这一条要打断他的惯性,
          而惯性正是「上一眼看过了,这一眼就不看了」。
          取货前改地址不会出现这个标 —— 那时货还在店里,没人被误导。
      */}
      {data.addressChangedAt && (
        <div
          className="mt-1 inline-flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1
                     text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-300"
        >
          ⚠ 地址已更新 —— 按上面这个新地址送
        </div>
      )}

      <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-500">
        {data.zone && <span>{data.zone}</span>}
        {(data.packageCount ?? 0) > 1 && <span>{data.packageCount} 件</span>}
        {data.weightGram != null && <span>{(data.weightGram / 1000).toFixed(1)} kg</span>}
        {/*
          ★ 说不说、说什么,都交给 codLabel ——
            四个地方各写一遍三元判断的话,迟早有一处漏掉待定的单,
            而那一处上面「代收」整行不出现,司机据此认为这单不用收钱。
        */}
        {codLabel(cod, data.codPending, data.currency) && (
          <span className="text-amber-700 dark:text-amber-400">
            代收 {codLabel(cod, data.codPending, data.currency)}
          </span>
        )}
      </div>

      {payout > 0 && (
        <div className="mt-1 font-medium text-slate-900 dark:text-slate-100">
          {money(payout, data.currency)}
        </div>
      )}
    </div>
  );
}
